/**
 * @file useListTableState.js
 * @description Custom React hook for managing state and logic for paginated, sortable, and selectable tables.
 * @author Yaofu Lee
 */

import { useState, useEffect, useMemo } from "react";
import { stableSort } from "../utils/dataUtils";
import isEqual from "lodash/isEqual";
import { debugLog } from "../utils/debugUtils";

// Custom hook for managing state in any sortable/paginated/selectable table UI.
/**
 * Hook to manage selection, sorting, and pagination for list/table components.
 *
 * @param {Object} options
 * @param {Array} options.items
 * @param {string} [options.initialOrderBy="updatedAt"]
 * @param {string} [options.initialOrder="desc"]
 * @param {number} [options.initialRowsPerPage=10]
 * @param {number} [options.initialPage=1]
 * @param {boolean} [options.selectionMode=false]
 * @param {Array} [options.initialSelected=[]]
 * @param {Array} [options.selected] - controlled selected ids (parent keeps state)
 * @param {Function} [options.onSelectedChange] - called on selection change
 *
 * @example
 * const {
 *   order, orderBy, selected, page, rowsPerPage,
 *   handleRequestSort, handleSelectAllClick, handleClick,
 *   handleChangePage, handleChangeRowsPerPage, setSelected
 * } = useListTableState({ data: items, rowsPerPage: 10 });
 */
export function useListTableState({
	items,
	initialOrderBy = "updatedAt",
	initialOrder = "desc",
	initialRowsPerPage = 10,
	initialPage = 1, // 1-based
	selectionMode = false,
	initialSelected = [],
	selected: controlledSelected,
	onSelectedChange,
	// Optional map of accessors to compute sort values by column id
	// e.g. { number_of_items: (row) => row.auc_package_items?.length ?? 0 }
	sortAccessors,
}) {
	// Paging, sorting, selection, density
	const [order, setOrder] = useState(initialOrder);
	const [orderBy, setOrderBy] = useState(initialOrderBy);
	const [page, setPage] = useState(initialPage - 1); // 0-based internal page state
	const [pageM1, setPageM1] = useState(initialPage); // 1-based custom pagination display
	const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
	const [dense, setDense] = useState(false);

	// --- Selection ---
	const [internalSelected, setInternalSelected] = useState(initialSelected || []);

	// Always use controlled value if present, else internal state
	const selected = controlledSelected !== undefined ? controlledSelected : internalSelected;

	// If initialSelected prop changes (and not controlled), update local selection
	useEffect(() => {
		if (controlledSelected === undefined && !isEqual(internalSelected, initialSelected || [])) {
			setInternalSelected(initialSelected || []);
		}
	}, [internalSelected, initialSelected, controlledSelected]);

	// Update handler: call onSelectedChange if provided, else use local state
	const changeSelected = (newSelected) => {
		debugLog(4, "[useListTableState][PROCESS] changeSelected", { newSelected: newSelected });

		if (onSelectedChange) onSelectedChange(newSelected);
		else setInternalSelected(newSelected);
	};

	// Derived: sorted + paged rows

	// Helper to read value using accessor if provided
	const getVal = (row, key) => {
		const acc = sortAccessors?.[key];
		try {
			return typeof acc === "function" ? acc(row) : row?.[key];
		} catch {
			return row?.[key];
		}
	};

	// Comparator that supports accessors; falls back to string/numeric comparison
	const accessorComparator = useMemo(() => {
		const baseCompare = (a, b) => {
			const va = getVal(a, orderBy);
			const vb = getVal(b, orderBy);
			// null/undefined last
			const aNil = va === null || va === undefined;
			const bNil = vb === null || vb === undefined;
			if (aNil && bNil) return 0;
			if (aNil) return 1;
			if (bNil) return -1;
			if (typeof va === "number" && typeof vb === "number") return va - vb;
			// string/numeric-aware compare
			return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
		};
		// Respect asc/desc
		return (a, b) => (order === "desc" ? -1 : 1) * baseCompare(a, b);
	}, [order, orderBy, sortAccessors]);

	const visibleRows = useMemo(() => {
		// If no accessor for current orderBy, still works—getVal falls back to row[key]
		return stableSort(items, accessorComparator).slice(
			page * rowsPerPage,
			page * rowsPerPage + rowsPerPage
		);
	}, [items, accessorComparator, page, rowsPerPage]);

	const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - items.length) : 0;

	// --- Handlers ---

	/**
	 * Handle request to sort by a column.
	 * @param {Event} event
	 * @param {string} property
	 */
	const handleRequestSort = (_, property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	/**
	 * Handle page change for pagination.
	 * @param {Event} event
	 * @param {number} newPage
	 */
	const handleChangePage = (_, newPage) => {
		setPage(newPage);
		setPageM1(newPage + 1);
	};
	const handleChangePageM1 = (_, newPage) => {
		setPageM1(newPage);
		setPage(newPage - 1);
	};

	/**
	 * Handle change in rows per page for pagination.
	 * @param {Event} event
	 */
	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
		setPageM1(1);
	};
	// const handleChangeDense = (event) => setDense(event.target.checked);
	const handleChangeDense = (e, v) => {
		console.log("dense:", e?.target?.checked, v);
		// pick whichever is defined
		const next = v !== undefined ? v : !!e?.target?.checked;
		console.log("dense:", next);
		setDense(next);
	};

	/**
	 * Handle selecting all rows.
	 * @param {Event} event
	 */
	const handleSelectAllClick = (event) => {
		if (!selectionMode) return;
		// setSelected(event.target.checked ? items.map((n) => n.id) : []);
		if (event.target.checked) {
			const allIds = items.map((n) => n.id);
			changeSelected(allIds);
		} else {
			changeSelected([]);
		}
	};

	const isSelected = (id) => selected.indexOf(id) !== -1;
	// const isSelected = (id) => {
	// 	return getValuesByPropertyPath(selected, "id").indexOf(id) !== -1;
	// };

	/**
	 * Handle row click to select/deselect.
	 * @param {Event} event
	 * @param {string|number} id
	 */
	const checkHandler = (event, id) => {
		if (!selectionMode) return;
		const selectedIndex = selected.indexOf(id);
		let newSelected = [];
		if (selectedIndex === -1) newSelected = newSelected.concat(selected, id);
		else if (selectedIndex === 0) newSelected = newSelected.concat(selected.slice(1));
		else if (selectedIndex === selected.length - 1)
			newSelected = newSelected.concat(selected.slice(0, -1));
		else if (selectedIndex > 0)
			newSelected = newSelected.concat(
				selected.slice(0, selectedIndex),
				selected.slice(selectedIndex + 1)
			);
		// setSelected(newSelected);
		changeSelected(newSelected);

		debugLog(3, "[useListTableState][ACTION] checkHandler", { newSelected: newSelected });
	};

	// Expose reset for legacy code
	const resetSelected = (items = []) => {
		changeSelected(items);
	};

	return {
		order,
		setOrder,
		orderBy,
		setOrderBy,
		page,
		setPage,
		pageM1,
		setPageM1,
		rowsPerPage,
		setRowsPerPage,
		dense,
		setDense,
		selected, // <--- always up-to-date
		setSelected: changeSelected,
		resetSelected,
		visibleRows,
		emptyRows,
		// handlers
		handleRequestSort,
		handleChangePage,
		handleChangePageM1,
		handleChangeRowsPerPage,
		handleChangeDense,
		handleSelectAllClick,
		isSelected,
		checkHandler,
	};
}
